import { managementPermissions } from './config/managementPermissions';
import WhatsApp from './pages/WhatsApp';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import {
  createVisiteOfflinePersister,
  shouldPersistVisiteQuery,
  VISITE_OFFLINE_MAX_AGE,
  VISITE_OFFLINE_BUSTER,
} from '@offertify/query';
import { webOfflineStorage } from './utils/offlineStorage';
import { Toaster } from 'react-hot-toast';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import HostOnlyRoute from './components/HostOnlyRoute';
import PermissionRoute from './components/PermissionRoute';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ConfirmEmailChange from './pages/ConfirmEmailChange';
import CommercialDocuments from './pages/CommercialDocuments';
import OfferEditor from './pages/OfferEditor';
import Settings from './pages/Settings';
import FICCallback from './pages/FICCallback';
import AuditLogs from './pages/AuditLogs';
import Tenants from './pages/Tenants';
import TenantEditor from './pages/TenantEditor';
import Piani from './pages/Piani';
import Clients from './pages/Clients';
import CrmCreate from './pages/CrmCreate';
import ClientDetail from './pages/ClientDetail';
import Attivita from './pages/Attivita';
import Leads from './pages/Leads';
import LeadDetail from './pages/LeadDetail';
import LeadsPipeline from './pages/LeadsPipeline';
import LeadIntakeForms from './pages/LeadIntakeForms';
import LeadIntakeFormEditor from './pages/LeadIntakeFormEditor';
import LeadTags from './pages/LeadTags';
import ActivityTypes from './pages/ActivityTypes';
import Canali from './pages/Canali';
import SettoriMerceologici from './pages/SettoriMerceologici';
import LeadScoringRules from './pages/LeadScoringRules';
import LeadSegments from './pages/LeadSegments';
import LeadCampaigns from './pages/LeadCampaigns';
import LeadCampaignEditor from './pages/LeadCampaignEditor';
import LeadCampaignDetail from './pages/LeadCampaignDetail';
import Templates from './pages/Templates';
import Products from './pages/Products';
import Cataloghi from './pages/Cataloghi';
import ProductEditor from './pages/ProductEditor';
import PriceLists from './pages/PriceLists';
import PriceListEditor from './pages/PriceListEditor';
import Promotions from './pages/Promotions';
import PromotionEditor from './pages/PromotionEditor';
import DiscountAuthorizations from './pages/DiscountAuthorizations';
import DiscountApprovals from './pages/DiscountApprovals';
import EmailTemplates from './pages/EmailTemplates';
import Users from './pages/Users';
import Roles from './pages/Roles';
import Catalog from './pages/Catalog';
import Cart from './pages/Cart';
import MyOrders from './pages/MyOrders';
import OrderDetail from './pages/OrderDetail';
import CustomerProfile from './pages/CustomerProfile';
import TelegramSettings from './pages/TelegramSettings';
import WhatsAppSettings from './pages/WhatsAppSettings';
import Messages from './pages/Messages';
import Conversations from './pages/Conversations';
import MyProfile from './pages/MyProfile';
import SyncAdmin from './pages/SyncAdmin';
import SyncQueryExplorer from './pages/SyncQueryExplorer';
import SyncQueryStudio from './pages/SyncQueryStudio';
import OpenapiHostAdmin from './pages/OpenapiHostAdmin';
import { AuthProvider } from './contexts/AuthContext';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Offline cache: persist only the Visite queries to localStorage (behind the
// shared storage abstraction) so the field agent can still consult visits with
// no connectivity. Non-visite queries are never written to storage.
const visiteOfflinePersister = createVisiteOfflinePersister(webOfflineStorage);

function App() {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: visiteOfflinePersister,
        maxAge: VISITE_OFFLINE_MAX_AGE,
        buster: VISITE_OFFLINE_BUSTER,
        dehydrateOptions: { shouldDehydrateQuery: shouldPersistVisiteQuery },
      }}
    >
      <AuthProvider>
        <Router>
          <div className="min-h-screen bg-gray-50">
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/confirm-email-change" element={<ConfirmEmailChange />} />

              {/* Protected routes */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="offers" element={
                  <PermissionRoute tenantOnly permission="offerte:view">
                    <CommercialDocuments />
                  </PermissionRoute>
                } />
                <Route path="sales-documents/:documentId?" element={<PermissionRoute tenantOnly permission="offerte:view"><CommercialDocuments /></PermissionRoute>} />
                <Route path="offers/new" element={
                  <PermissionRoute anyOf={["offerte:create"]}>
                    <OfferEditor />
                  </PermissionRoute>
                } />
                <Route path="offers/:id/edit" element={
                  <PermissionRoute anyOf={["offerte:edit", "offerte:edit_all"]}>
                    <OfferEditor />
                  </PermissionRoute>
                } />
                <Route path="discount-approvals" element={
                  <PermissionRoute permission="offerte:approve_discount">
                    <DiscountApprovals />
                  </PermissionRoute>
                } />
                <Route path="clients" element={
                  <PermissionRoute permission="clienti:view">
                    <Clients />
                  </PermissionRoute>
                } />
                <Route path="clients/new" element={
                  <PermissionRoute permission="clienti:create">
                    <CrmCreate kind="client" />
                  </PermissionRoute>
                } />
                <Route path="clients/:id" element={
                  <PermissionRoute permission="clienti:view">
                    <ClientDetail />
                  </PermissionRoute>
                } />
                <Route path="attivita" element={
                  <PermissionRoute permission="visite:view">
                    <Attivita />
                  </PermissionRoute>
                } />
                <Route path="leads" element={
                  <PermissionRoute permission="leads:view">
                    <Leads />
                  </PermissionRoute>
                } />
                <Route path="leads/pipeline" element={
                  <PermissionRoute permission="leads:view">
                    <LeadsPipeline />
                  </PermissionRoute>
                } />
                <Route path="leads/intake-forms" element={
                  <PermissionRoute anyOf={managementPermissions['leads:config']}>
                    <LeadIntakeForms />
                  </PermissionRoute>
                } />
                <Route path="leads/intake-forms/new" element={
                  <PermissionRoute anyOf={managementPermissions['leads:config']}>
                    <LeadIntakeFormEditor />
                  </PermissionRoute>
                } />
                <Route path="leads/intake-forms/:id/edit" element={
                  <PermissionRoute anyOf={managementPermissions['leads:config']}>
                    <LeadIntakeFormEditor />
                  </PermissionRoute>
                } />
                <Route path="leads/tags" element={
                  <PermissionRoute anyOf={managementPermissions['leads:config']}>
                    <LeadTags />
                  </PermissionRoute>
                } />
                <Route path="leads/scoring-rules" element={
                  <PermissionRoute anyOf={managementPermissions['leads:config']}>
                    <LeadScoringRules />
                  </PermissionRoute>
                } />
                <Route path="leads/segments" element={
                  <PermissionRoute anyOf={managementPermissions['leads:config']}>
                    <LeadSegments />
                  </PermissionRoute>
                } />
                <Route path="leads/campaigns" element={
                  <PermissionRoute anyOf={managementPermissions['leads:config']}>
                    <LeadCampaigns />
                  </PermissionRoute>
                } />
                <Route path="leads/campaigns/new" element={
                  <PermissionRoute anyOf={managementPermissions['leads:config']}>
                    <LeadCampaignEditor />
                  </PermissionRoute>
                } />
                <Route path="leads/campaigns/:id/edit" element={
                  <PermissionRoute anyOf={managementPermissions['leads:config']}>
                    <LeadCampaignEditor />
                  </PermissionRoute>
                } />
                <Route path="leads/campaigns/:id" element={
                  <PermissionRoute anyOf={managementPermissions['leads:config']}>
                    <LeadCampaignDetail />
                  </PermissionRoute>
                } />
                <Route path="leads/new" element={
                  <PermissionRoute permission="leads:create">
                    <CrmCreate kind="lead" />
                  </PermissionRoute>
                } />
                <Route path="leads/:id" element={
                  <PermissionRoute permission="leads:view">
                    <LeadDetail />
                  </PermissionRoute>
                } />
                <Route path="cataloghi" element={<PermissionRoute permission="cataloghi:view"><Cataloghi /></PermissionRoute>} />
                <Route path="products" element={
                  <PermissionRoute permission="prodotti:view">
                    <Products />
                  </PermissionRoute>
                } />
                <Route path="products/new" element={
                  <PermissionRoute permission="prodotti:create">
                    <ProductEditor />
                  </PermissionRoute>
                } />
                <Route path="products/:id/edit" element={
                  <PermissionRoute permission="prodotti:view">
                    <ProductEditor />
                  </PermissionRoute>
                } />
                <Route path="price-lists" element={
                  <PermissionRoute permission="listini:view">
                    <PriceLists />
                  </PermissionRoute>
                } />
                <Route path="price-lists/new" element={
                  <PermissionRoute permission="listini:create">
                    <PriceListEditor />
                  </PermissionRoute>
                } />
                <Route path="price-lists/:id" element={
                  <PermissionRoute anyOf={["listini:edit", "listini:view"]}>
                    <PriceListEditor />
                  </PermissionRoute>
                } />
                <Route path="promotions" element={
                  <PermissionRoute permission="promozioni:view">
                    <Promotions />
                  </PermissionRoute>
                } />
                <Route path="promotions/new" element={
                  <PermissionRoute permission="promozioni:create">
                    <PromotionEditor />
                  </PermissionRoute>
                } />
                <Route path="promotions/:id" element={
                  <PermissionRoute anyOf={["promozioni:edit", "promozioni:view"]}>
                    <PromotionEditor />
                  </PermissionRoute>
                } />
                <Route path="discount-authorizations" element={
                  <PermissionRoute anyOf={managementPermissions['autorizzazioni']}>
                    <DiscountAuthorizations />
                  </PermissionRoute>
                } />
                <Route path="email-templates" element={
                  <PermissionRoute anyOf={managementPermissions['email_templates']}>
                    <EmailTemplates />
                  </PermissionRoute>
                } />
                <Route path="templates" element={
                  <PermissionRoute anyOf={managementPermissions['template']}>
                    <Templates />
                  </PermissionRoute>
                } />
                <Route path="activity-types" element={
                  <PermissionRoute anyOf={managementPermissions['activity_types']}>
                    <ActivityTypes />
                  </PermissionRoute>
                } />
                <Route path="canali" element={
                  <PermissionRoute anyOf={managementPermissions['canali']}>
                    <Canali />
                  </PermissionRoute>
                } />
                <Route path="settori-merceologici" element={
                  <PermissionRoute anyOf={managementPermissions['settori']}>
                    <SettoriMerceologici />
                  </PermissionRoute>
                } />
                <Route path="tenants" element={
                  <HostOnlyRoute>
                    <Tenants />
                  </HostOnlyRoute>
                } />
                <Route path="tenants/new" element={
                  <HostOnlyRoute>
                    <TenantEditor />
                  </HostOnlyRoute>
                } />
                <Route path="tenants/:id/edit" element={
                  <HostOnlyRoute>
                    <TenantEditor />
                  </HostOnlyRoute>
                } />
                <Route path="piani" element={
                  <HostOnlyRoute>
                    <Piani />
                  </HostOnlyRoute>
                } />
                <Route path="telegram-settings" element={
                  <HostOnlyRoute>
                    <TelegramSettings />
                  </HostOnlyRoute>
                } />
                <Route path="sync-admin" element={
                  <PermissionRoute permission="sync:manage">
                    <SyncAdmin />
                  </PermissionRoute>
                } />
                <Route path="sync-admin/query/new" element={
                  <PermissionRoute permission="sync:manage">
                    <SyncQueryStudio />
                  </PermissionRoute>
                } />
                <Route path="sync-admin/query/:queryId/edit" element={
                  <PermissionRoute permission="sync:manage">
                    <SyncQueryStudio />
                  </PermissionRoute>
                } />
                <Route path="sync-query-explorer" element={
                  <HostOnlyRoute>
                    <SyncQueryExplorer />
                  </HostOnlyRoute>
                } />
                <Route path="host/openapi" element={
                  <HostOnlyRoute>
                    <OpenapiHostAdmin />
                  </HostOnlyRoute>
                } />
                <Route path="whatsapp" element={<WhatsApp />} />
                <Route path="whatsapp-settings" element={
                  <PermissionRoute permission="settings:integrations">
                    <WhatsAppSettings />
                  </PermissionRoute>
                } />
                <Route path="messages" element={<Messages />} />
                <Route path="conversations" element={<Conversations />} />
                <Route path="users" element={
                  <PermissionRoute permission="users:view">
                    <Users />
                  </PermissionRoute>
                } />
                <Route path="roles" element={
                  <PermissionRoute permission="users:view">
                    <Roles />
                  </PermissionRoute>
                } />
                <Route path="audit" element={
                  <PermissionRoute permission="audit:view">
                    <AuditLogs />
                  </PermissionRoute>
                } />
                <Route path="my-profile" element={<MyProfile />} />
                <Route path="settings" element={
                  <PermissionRoute permission="settings:view">
                    <Settings />
                  </PermissionRoute>
                } />
                <Route path="settings/fic/callback" element={<FICCallback />} />
                {/* Portal Cliente routes */}
                <Route path="catalog" element={
                  <PermissionRoute roles={['cliente']}>
                    <Catalog />
                  </PermissionRoute>
                } />
                <Route path="cart" element={
                  <PermissionRoute roles={['cliente']}>
                    <Cart />
                  </PermissionRoute>
                } />
                <Route path="my-orders" element={
                  <PermissionRoute roles={['cliente']}>
                    <MyOrders />
                  </PermissionRoute>
                } />
                <Route path="my-orders/:id" element={
                  <PermissionRoute roles={['cliente']}>
                    <OrderDetail />
                  </PermissionRoute>
                } />
                <Route path="profile" element={
                  <PermissionRoute roles={['cliente']}>
                    <CustomerProfile />
                  </PermissionRoute>
                } />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Routes>
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
              }}
            />
          </div>
        </Router>
      </AuthProvider>
    </PersistQueryClientProvider>
  );
}

export default App;
